/**
 * How each table is stored encrypted (ADR-0021).
 *
 * Plaintext is limited to what IndexedDB must index or what the app needs
 * without decrypting, none of it survey content:
 *   - primary keys (UUIDs, kv keys like "pack.serverTime");
 *   - findings.siteId, drafts.siteId, attachments.surveyId, outbox.entityId (UUIDs, for lookups);
 *   - drafts.updatedAt, outbox.seq (ordering);
 *   - drafts.templateVersionId (which template versions to keep on a pack refresh).
 * Everything else — answers, notes, flags, photos and files, outbox payloads,
 * carried findings, site names/addresses/coordinates, customers, cached
 * My work — is inside an AES-GCM envelope bound to "<table>:<key>".
 *
 * Template definitions stay plaintext: they are the same questions for every
 * device and contain no site or customer data.
 */
import type { OutboxItem } from '@sss/sync';
import type { CarriedFinding, PackCustomer, PackSite } from '../api/types';
import type { Envelope, RecordCipher } from '../security/cipher';
import type { AttachmentRecord, DraftRecord } from './types';

export interface SealedRow {
  id: string;
  enc: Envelope;
}
export interface SealedFindingRow extends SealedRow {
  siteId: string;
}
export interface SealedDraftRow extends SealedRow {
  siteId: string;
  updatedAt: number;
  templateVersionId: string;
}
export interface SealedAttachmentRow {
  id: string;
  surveyId: string;
  meta: Envelope;
  body: Envelope;
}
export interface SealedOutboxRow extends SealedRow {
  entityId: string;
  seq: number;
}

export const aad = (table: string, key: string) => `${table}:${key}`;

export async function sealSite(c: RecordCipher, s: PackSite): Promise<SealedRow> {
  return { id: s.id, enc: await c.sealJson(s, aad('sites', s.id)) };
}
export function openSite(c: RecordCipher, r: SealedRow): Promise<PackSite> {
  return c.openJson<PackSite>(r.enc, aad('sites', r.id));
}

export async function sealCustomer(c: RecordCipher, x: PackCustomer): Promise<SealedRow> {
  return { id: x.id, enc: await c.sealJson(x, aad('customers', x.id)) };
}
export function openCustomer(c: RecordCipher, r: SealedRow): Promise<PackCustomer> {
  return c.openJson<PackCustomer>(r.enc, aad('customers', r.id));
}

export async function sealFinding(c: RecordCipher, f: CarriedFinding): Promise<SealedFindingRow> {
  return { id: f.id, siteId: f.siteId, enc: await c.sealJson(f, aad('findings', f.id)) };
}
export function openFinding(c: RecordCipher, r: SealedFindingRow): Promise<CarriedFinding> {
  return c.openJson<CarriedFinding>(r.enc, aad('findings', r.id));
}

export async function sealDraft(c: RecordCipher, d: DraftRecord): Promise<SealedDraftRow> {
  return { id: d.id, siteId: d.siteId, updatedAt: d.updatedAt, templateVersionId: d.templateVersionId, enc: await c.sealJson(d, aad('drafts', d.id)) };
}
export function openDraft(c: RecordCipher, r: SealedDraftRow): Promise<DraftRecord> {
  return c.openJson<DraftRecord>(r.enc, aad('drafts', r.id));
}

export async function sealAttachment(c: RecordCipher, a: AttachmentRecord): Promise<SealedAttachmentRow> {
  const { data, ...meta } = a;
  return {
    id: a.id,
    surveyId: a.surveyId,
    meta: await c.sealJson(meta, aad('attachments.meta', a.id)),
    body: await c.seal(data, aad('attachments.body', a.id)),
  };
}
export async function openAttachment(c: RecordCipher, r: SealedAttachmentRow): Promise<AttachmentRecord> {
  const meta = await c.openJson<Omit<AttachmentRecord, 'data'>>(r.meta, aad('attachments.meta', r.id));
  return { ...meta, data: await c.open(r.body, aad('attachments.body', r.id)) };
}

export async function sealOutbox(c: RecordCipher, i: OutboxItem): Promise<SealedOutboxRow> {
  return { id: i.id, entityId: i.entityId, seq: i.seq, enc: await c.sealJson(i, aad('outbox', i.id)) };
}
export function openOutbox(c: RecordCipher, r: SealedOutboxRow): Promise<OutboxItem> {
  return c.openJson<OutboxItem>(r.enc, aad('outbox', r.id));
}

export function sealMeta(c: RecordCipher, key: string, value: unknown): Promise<Envelope> {
  return c.sealJson(value, aad('kv', key));
}
export function openMeta<T>(c: RecordCipher, key: string, env: Envelope): Promise<T> {
  return c.openJson<T>(env, aad('kv', key));
}
